import { useState, useEffect, useCallback } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { needsProfession } from '@/lib/role-nudge'
import { useRecordingStore } from '@/stores/recording'
import { useRecordingPrefs } from '@/stores/recordingPrefs'
// Call store imported dynamically to avoid pulling LiveKit into the main bundle
const startMeeting = () => import('@/stores/call').then(m => m.useCallStore.getState().startMeeting({ title: 'Consultation' }))
import { useWsStore } from '@/stores/websocket'
import { api } from '@/api/client'
import { cn } from '@/utils/cn'
import {
  LayoutDashboard,
  Video,
  Mic,
  LogOut,
  Globe,
  X,
  Plus,
  MessageSquarePlus,
  CircleHelp,
  Lock as LockIcon,
} from 'lucide-react'
import { useMessagingStore } from '@/stores/messaging'
import { useFeedbackStore } from '@/features/feedback'
import { useBookingConfirmations } from '@/api/bookings-hooks'
import { confirm } from '@/components/shared/ConfirmDialog'
import { useSubscription } from '@/api/subscription-hooks'
import { TrialStrip } from './TrialStrip'
import { openUpgradePanel } from '@/lib/upgrade'
import { navItemsFor } from './navItems'
import ClaroLogo from '@/components/shared/ClaroLogo'
import { regionalText } from '@/lib/regional-copy'

const adminItems = [
  { to: '/manage', label: 'Manage', icon: LayoutDashboard },
]

export function Sidebar({ mobile, onClose }: { mobile?: boolean; onClose?: () => void } = {}) {
  const {
    user, clinics, activeClinicId, isPlatformAdmin, isPlatformStaff, hasPermission, canManageClinic,
    activeClinicRole, isReadOnly, switchClinic, logout,
  } = useAuthStore()
  // Same gate as ManageLayout's redirect, so the link never leads to a bounce.
  const canManage = isPlatformAdmin() || isPlatformStaff() || hasPermission('settings:manage') || hasPermission('orgs:manage') || hasPermission('network:manage') || hasPermission('sales:read')
  // Mirrors the registry's own permission filter (CLA-1307): money reports
  // need sales:read, schedule reports settings:manage; either is enough to
  // be offered something.
  const canReport = isPlatformAdmin() || hasPermission('sales:read') || hasPermission('settings:manage')
  const navItems = navItemsFor({ canOrder: canManageClinic(), canReport, clinicRole: activeClinicRole() })
  const readOnly = isReadOnly()
  const wsStatus = useWsStore((s) => s.status)
  const recording = useRecordingStore()
  const totalUnread = useMessagingStore((s) => s.totalUnread)
  const { data: confirmationsData } = useBookingConfirmations()
  // Fail-open: until the subscription loads (or if it errors), nothing locks.
  const { data: subscription } = useSubscription()
  const planFeatures = subscription?.features
  const pendingCount =
    confirmationsData?.participants.filter((p) => p.status === 'pending').length ?? 0
  // CLA-1141 follow-up: a quiet dot on Settings when the profession is unset.
  // The banner snoozes; this doesn't — it's the wayfinding half, and without it
  // the Settings flag is only ever found by someone already going there.
  const flagSettings = needsProfession(user)
  const navigate = useNavigate()
  const location = useLocation()

  const handleRecord = () => {
    if (recording.state !== 'idle') return
    if (location.pathname !== '/') {
      navigate('/')
    }
    const profileId = useRecordingPrefs.getState().effectiveProfileId() ?? undefined
    // Small delay to ensure we're on the notes page before starting
    setTimeout(() => recording.startRecording({ profileId }), location.pathname !== '/' ? 100 : 0)
  }

  const openFeedback = useFeedbackStore((s) => s.open)

  // Switching the active clinic is a single click in the sidebar, which is easy
  // to trigger by mistake — and a stray switch silently re-scopes where new
  // notes and patients get saved. Add a deliberate checkpoint that names the
  // consequence. No-op when the clinic is already active.
  const handleSwitchClinic = async (clinic: { id: number; name: string }) => {
    if (clinic.id === activeClinicId) return
    const ok = await confirm(`Switch to ${clinic.name}?`, {
      description: regionalText('New notes and patients you create will be saved under {name} until you switch clinics again.', { name: clinic.name }),
      confirmLabel: 'Switch',
    })
    if (ok) await switchClinic(clinic.id)
  }

  return (
    <aside className={cn(
      // overflow-y-auto so the drawer scrolls on short viewports (mobile
      // landscape / phones with the address bar visible); on tall desktop
      // viewports there's nothing to scroll and the bar reads identically.
      // Pale Grey canvas with a Mist edge, as in the brand's web mockup (CLA-1817).
      'flex flex-col bg-surface-secondary border-r border-border h-dvh shrink-0 overflow-y-auto',
      mobile ? 'w-64' : 'hidden lg:flex w-52 sticky top-0',
    )}>
      {/* Logo + mobile close */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between">
        <h1 className="leading-none"><ClaroLogo size={22} /></h1>
        {mobile && onClose && (
          <button onClick={onClose} className="p-1 -mr-1 rounded-lg hover:bg-claro-100/60 text-ink-secondary transition-colors" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav aria-label="Main navigation" className="flex-1 px-3 space-y-0.5">
        {navItems.map(({ to, label, icon: Icon, feature }) => {
          const unread =
            label === 'Messages'
              ? totalUnread
              : label === 'Confirmations'
                ? pendingCount
                : 0
          // CLA-980: Order locks on the store unlock flag, not the plan
          // feature — standard plans carry "store" but the store stays
          // locked until the clinic is deliberately unlocked. It stays a
          // link (padlocked) to the locked page, not the upgrade panel:
          // upgrading doesn't unlock it. Only PLATFORM admins bypass, as on
          // the server — `isAdmin` matches any named admin grant, which
          // every clinic owner holds (CLA-993).
          const storeLocked =
            to === '/order' && !hasPermission('admin:*') && subscription?.store_unlocked === false
          const locked =
            to !== '/order' && Boolean(feature && planFeatures && !planFeatures.includes(feature))
          if (storeLocked) {
            return (
              <NavLink
                key={to}
                to={to}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 text-ink-faint hover:text-ink hover:bg-claro-100/60"
              >
                <Icon className="w-4 h-4" />
                {label}
                <LockIcon className="ml-auto w-3 h-3" aria-label="Not available yet" />
              </NavLink>
            )
          }
          if (locked) {
            return (
              <button
                key={to}
                type="button"
                onClick={() => openUpgradePanel(feature!)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 text-ink-faint hover:text-ink hover:bg-claro-100/60"
              >
                <Icon className="w-4 h-4" />
                {label}
                <LockIcon className="ml-auto w-3 h-3" aria-label="Requires an upgraded plan" />
              </button>
            )
          }
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150',
                  isActive
                    ? 'bg-primary-soft text-on-primary-soft font-medium'
                    : 'text-ink-secondary hover:text-ink hover:bg-claro-100/60',
                )
              }
            >
              <Icon className="w-4 h-4" />
              {label}
              {unread > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-claro-600 text-white text-[10px] font-medium flex items-center justify-center">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
              {to === '/settings' && flagSettings && (
                <span
                  className="ml-auto w-1.5 h-1.5 rounded-full bg-warning"
                  aria-label="Your role isn't set"
                />
              )}
            </NavLink>
          )
        })}

        {canManage &&
          adminItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150',
                  isActive
                    ? 'bg-primary-soft text-on-primary-soft font-medium'
                    : 'text-ink-secondary hover:text-ink hover:bg-claro-100/60',
                )
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
      </nav>

      {/* Action buttons — a viewer reads; the mic would only lead to a refusal */}
      <div className="px-3 pb-3 space-y-0.5">
        {!readOnly && (
        <button
          onClick={handleRecord}
          disabled={recording.state === 'recording' || recording.state === 'paused'}
          // Red means recording (John, 2026-10-17): the entry point is a red
          // mic with an Ink label, not a filled pill. Unfilled, it can't read
          // as the selected nav item, and the notes header's round button
          // stays the one red fill on screen.
          className={cn(
            'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
            recording.state === 'recording'
              ? 'text-ink cursor-default'
              : recording.state === 'paused'
                ? 'text-ink-secondary cursor-default'
                : 'text-ink hover:bg-claro-100/60',
          )}
        >
          <Mic className={cn('w-4 h-4', recording.state === 'paused' ? 'text-ink-secondary' : 'text-record')} />
          {recording.state === 'paused' ? 'Paused' : recording.state === 'recording' ? 'Listening…' : 'Record'}
        </button>
        )}
        <button
          onClick={() => startMeeting()}
          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-ink-secondary hover:text-ink hover:bg-claro-100/60 transition-colors"
        >
          <Video className="w-4 h-4" />
          Consultation
        </button>
      </div>

      {/* Screening availability toggle — prescribers only */}
      {user?.prescriber && (
        <ScreeningAvailability />
      )}

      {/* Clinic switcher — visible whenever the user has any clinic, so the
          "Add clinic" affordance is always reachable. Multi-clinic users
          get the full list with the active one marked; a single-clinic user
          gets the clinic's name (CLA-1633: otherwise nothing on screen says
          where notes and patients are going), plain text with nothing to
          switch to, then the add link. */}
      {clinics.length >= 1 && (
        <div className="px-3 pb-3 border-t border-border pt-3">
          {clinics.length === 1 && (
            <p
              data-testid="active-clinic-name"
              title={clinics[0].name}
              className="px-2 py-1 text-xs font-medium text-ink-secondary truncate"
            >
              <span
                aria-hidden="true"
                className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                style={{ backgroundColor: clinics[0].primary_color || 'var(--clinic-accent)' }}
              />
              {clinics[0].name}
            </p>
          )}
          {clinics.length > 1 && (
            <p className="text-[10px] font-medium text-ink-faint uppercase tracking-wider px-2 mb-1.5">
              {regionalText('Your clinics')}
            </p>
          )}
          {clinics.length > 1 &&
            clinics.map((clinic) => (
              <button
                key={clinic.id}
                onClick={() => handleSwitchClinic(clinic)}
                className={cn(
                  'block w-full text-left px-2 py-1 text-xs rounded transition-colors truncate',
                  clinic.id === activeClinicId
                    ? 'text-claro-700 font-medium'
                    : 'text-ink-tertiary hover:text-ink',
                )}
              >
                {clinic.id === activeClinicId && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                    style={{ backgroundColor: clinic.primary_color || 'var(--clinic-accent)' }}
                  />
                )}
                {clinic.name}
              </button>
            ))}
          <button
            onClick={() => navigate('/onboarding/another-clinic')}
            className="flex items-center gap-1.5 w-full text-left px-2 py-1 text-xs text-ink-tertiary hover:text-ink rounded transition-colors"
          >
            <Plus className="w-3 h-3" />
            <span>{regionalText('Add clinic')}</span>
          </button>
        </div>
      )}

      {/* CLA-1010: the trial's state AND its price, without anyone going
          looking. Renders nothing unless the clinic is genuinely trialing. */}
      <TrialStrip onNavigate={onClose} />

      {/* Keyboard shortcuts hint */}
      <div className="px-4 py-2 border-t border-border">
        <button
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }))}
          className="flex items-center justify-between w-full text-[10px] text-ink-faint hover:text-ink transition-colors"
          title="Keyboard shortcuts"
        >
          <span>Shortcuts</span>
          <div className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-surface border border-border rounded text-[9px] font-mono">?</kbd>
            <kbd className="px-1 py-0.5 bg-surface border border-border rounded text-[9px] font-mono">⌘K</kbd>
          </div>
        </button>
      </div>

      {/* Help (CLA-1838) and Send feedback — always visible, both also in ⌘K */}
      <div className="px-3 pb-2">
        <NavLink
          to={location.pathname.startsWith('/help') ? '/help' : `/help?from=${encodeURIComponent(location.pathname)}`}
          onClick={() => onClose?.()}
          className={({ isActive }) => cn(
            'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-colors',
            isActive || location.pathname.startsWith('/help')
              ? 'text-claro-700 bg-claro-50 font-medium'
              : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary',
          )}
        >
          <CircleHelp className="w-4 h-4" />
          Help
        </NavLink>
        <button
          onClick={() => {
            openFeedback({ entryPoint: 'profile_menu' })
            onClose?.()
          }}
          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-ink-secondary hover:text-ink hover:bg-claro-100/60 transition-colors"
        >
          <MessageSquarePlus className="w-4 h-4" />
          Send feedback
        </button>
      </div>

      {/* User + status + sign out */}
      <div className="px-4 py-3 border-t border-border">
        <p className="text-xs text-ink-tertiary truncate">{user?.email}</p>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full',
                wsStatus === 'connected' ? 'bg-success' :
                wsStatus === 'connecting' ? 'bg-warning' :
                wsStatus === 'auth_failed' ? 'bg-error' :
                'bg-claro-200',
              )}
            />
            <span className={cn(
              'text-[10px]',
              wsStatus === 'auth_failed' ? 'text-on-error-soft' : 'text-ink-faint',
            )}>
              {wsStatus === 'connected' ? 'Connected' :
               wsStatus === 'connecting' ? 'Connecting...' :
               wsStatus === 'auth_failed' ? 'Session expired' :
               'Offline'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={logout}
              className="text-[10px] text-ink-faint hover:text-ink transition-colors flex items-center gap-1"
              title="Sign out"
            >
              <LogOut className="w-3 h-3" />
              Sign out
            </button>
          </div>
        </div>
        {/* Region switcher — platform staff only. Full-page navigation so the
            edge Worker sets the claro_realm cookie and routes the realm. */}
        {hasPermission('admin:*') && (
          <div className="flex items-center gap-1.5 mt-2" title="Switch platform region">
            <Globe className="w-3 h-3 text-ink-faint" />
            <button
              onClick={() => window.location.assign('/?claro_realm=au')}
              className="text-[10px] text-ink-faint hover:text-ink transition-colors"
            >
              Australia
            </button>
            <span className="text-[10px] text-ink-faint">·</span>
            <button
              onClick={() => window.location.assign('/?claro_realm=us')}
              className="text-[10px] text-ink-faint hover:text-ink transition-colors"
            >
              United States
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

// --- Screening availability toggle (doctors/prescribers) ---

function ScreeningAvailability() {
  const [status, setStatus] = useState<string>('offline')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    api.get<{ status?: string }>('/telehealth/availability')
      .then(d => { setStatus(d.status ?? 'offline'); setLoaded(true) })
      .catch(() => setLoaded(true))
  }, [])

  const toggle = useCallback(async () => {
    const newStatus = status === 'available' ? 'offline' : 'available'
    setStatus(newStatus)
    await api.put('/telehealth/availability', { status: newStatus })
  }, [status])

  if (!loaded) return null

  return (
    <div className="px-3 pb-3">
      <button
        onClick={toggle}
        className={cn(
          'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
          status === 'available'
            ? 'bg-success/10 text-on-success-soft hover:bg-success/20'
            : 'bg-surface text-ink-faint hover:text-ink hover:bg-claro-100/60'
        )}
      >
        <span className={cn(
          'w-2 h-2 rounded-full shrink-0',
          status === 'available' ? 'bg-success' : 'bg-claro-200'
        )} />
        {status === 'available' ? 'Available' : 'Screening off'}
      </button>
    </div>
  )
}
